import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import CardForm, { type CardFormValues } from '../components/CardForm';

export default function CardEditorPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { items, addItem, updateItem } = useSafe();
  const existing = id ? items.find((i) => i.id === id && i.type === 'card' && !i.orgId) : undefined;
  const isEdit = !!existing;
  const [loading, setLoading] = useState(false);

  async function handleSubmit(values: CardFormValues): Promise<void> {
    setLoading(true);
    try {
      const payload = {
        type: 'card' as const, ...values,
        // Cards behave as `sensitive` throughout (spec §4.4): a card copy/reveal
        // is treated like a sensitive login for usage events, and the masked
        // display follows the same path.
        sensitive: true, favorite: existing?.favorite ?? false, tags: existing?.tags ?? [],
      };
      if (isEdit && existing) { await updateItem(existing.id, payload); navigate(`/cards/${existing.id}`); }
      else { const created = await addItem(payload); navigate(`/cards/${created.id}`); }
    } finally { setLoading(false); }
  }

  return (
    <CardForm
      // Remount on route change so useState field seeds re-run. Both
      // /cards/new and /cards/:id/edit render this page into the same
      // persistent CardsLayout outlet; without a key, React reuses the
      // CardForm instance and a prior card's data lingers when you switch to
      // "+ New" (or between two cards).
      key={id ?? 'new'}
      heading={isEdit && existing ? `Edit ${existing.name}` : 'New Payment Card'}
      initial={existing} isEdit={isEdit} loading={loading}
      onSubmit={handleSubmit}
      onCancel={() => navigate(isEdit && existing ? `/cards/${existing.id}` : '/cards')}
    />
  );
}
